import { useHudStore } from "../../store/hudStore";
import { DOMAINS, heatColor, peso } from "../../data/seed";
import { renderInline } from "./inline";
import type { HudStore } from "../../store/hudStore";
import type { NoteBlock as NoteBlockT } from "../../types";

const mono = "ui-monospace, SFMono-Regular, Menlo, monospace";

// Dataview rows, ported from app.html dvRows(). The vault browses in the source's
// empty (day-one) context, so character/quests/domains render their empty state;
// debts reflect whatever finance data is loaded.
interface DvRow { a: string; b: string; c: string; color: string }
function dvRows(st: HudStore, kind: string | undefined): DvRow[] {
  if (kind === "character") return [{ a: "Arlou", b: "level 1", c: "0 / 500 XP", color: "var(--accent-ink)" }];
  if (kind === "quests") return [];
  if (kind === "domains") return DOMAINS.map((d) => ({ a: d.name, b: d.last, c: "0 XP", color: heatColor(d.health) }));
  if (kind === "debts") return st.debts.map((d) => ({ a: d.name, b: d.next, c: peso(d.balance), color: d.color }));
  return [];
}

// One parsed note block (app.html noteBlockHtml). Rendered as JSX - no raw-HTML
// injection, so no sanitizer needed.
export default function NoteBlock({ block: b }: { block: NoteBlockT }) {
  const st = useHudStore();
  const text = b.text ?? "";

  if (b.k === "h") return <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.02em", textWrap: "pretty" }}>{renderInline(text)}</div>;
  if (b.k === "p") return <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--sub)", lineHeight: 1.6, textWrap: "pretty" }}>{renderInline(text)}</div>;
  if (b.k === "li")
    return (
      <div style={{ display: "flex", alignItems: "flex-start", gap: 11, fontSize: 13.5, fontWeight: 600, color: "var(--sub)", lineHeight: 1.6 }}>
        <b style={{ width: 5, height: 5, borderRadius: 3, background: "var(--accent)", marginTop: 8, flex: "0 0 auto" }} />
        <span style={{ minWidth: 0 }}>{renderInline(text)}</span>
      </div>
    );
  if (b.k === "task")
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
        <span style={{ width: 18, height: 18, flex: "0 0 auto", display: "grid", placeItems: "center", borderRadius: 5, background: b.done ? "var(--accent)" : "transparent", border: b.done ? "1.6px solid transparent" : "1.6px solid var(--control-edge)" }}>
          <i className="fa-solid fa-check" style={{ fontSize: 9, lineHeight: 1, color: "var(--on-accent)", opacity: b.done ? 1 : 0 }} />
        </span>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: b.done ? "var(--mut)" : "var(--sub)", textDecoration: b.done ? "line-through" : "none" }}>{renderInline(text)}</span>
      </div>
    );
  if (b.k === "code")
    return <div style={{ fontFamily: mono, fontSize: 12, lineHeight: 1.7, color: "var(--green-ink)", background: "var(--bg2)", border: "1px solid var(--line)", borderRadius: 8, padding: 14, whiteSpace: "pre-wrap", overflowX: "auto" }}>{text}</div>;
  if (b.k === "dv") {
    const rows = dvRows(st, b.kind);
    return (
      <div style={{ border: "1px solid var(--line2)", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 13px", background: "var(--card2)", fontFamily: mono, fontSize: 11, fontWeight: 600, color: "var(--mut)", overflowX: "auto", whiteSpace: "nowrap" }}>
          <i className="fa-solid fa-bolt" style={{ fontSize: 10, lineHeight: 1, color: "var(--accent)" }} />
          {b.query}
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {rows.map((dr, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 13px", borderTop: "1px solid var(--line)" }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink)", minWidth: 0, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{dr.a}</span>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--mut)", flex: "0 0 auto" }}>{dr.b}</span>
              <span style={{ fontSize: 12.5, fontWeight: 700, flex: "0 0 auto", minWidth: 62, textAlign: "right", color: dr.color }}>{dr.c}</span>
            </div>
          ))}
          {rows.length === 0 && <div style={{ padding: "18px 13px", borderTop: "1px solid var(--line)", fontSize: 12.5, fontWeight: 600, color: "var(--mut)" }}>No rows - the query is honest about an empty vault.</div>}
        </div>
      </div>
    );
  }
  return null;
}
